import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { getSurveyFileQuestions } from 'src/api/dataVault/surveyFileQuestions';
import type { DocCard } from 'src/api/dataVault/surveyFileQuestions';

type Status = 'idle' | 'loading' | 'succeeded' | 'failed';

interface Entry {
  status: Status;
  cards: DocCard[];
  error: string | null;
  lastFetchedAt: number | null;
}

interface State {
  byKey: Record<string, Entry>;
}

const STALE_MS = 30_000;
const initialState: State = { byKey: {} };

function cacheKey(surveyId: number, actingOnMemberId: number | null): string {
  return `${surveyId}:${actingOnMemberId ?? 'self'}`;
}

export const fetchFileQuestions = createAsyncThunk(
  'surveyFileQuestions/fetch',
  async (
    {
      surveyId,
      actingOnMemberId = null,
      forceRefresh = false,
    }: {
      surveyId: number;
      actingOnMemberId?: number | null;
      forceRefresh?: boolean;
    },
    { getState, rejectWithValue }
  ) => {
    const key = cacheKey(surveyId, actingOnMemberId);
    const state = (getState() as { surveyFileQuestions: State })
      .surveyFileQuestions;
    const entry = state.byKey[key];
    if (
      !forceRefresh &&
      entry?.status === 'succeeded' &&
      entry.lastFetchedAt &&
      Date.now() - entry.lastFetchedAt < STALE_MS
    ) {
      return null;
    }
    try {
      return await getSurveyFileQuestions(surveyId, { actingOnMemberId });
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: string } } };
      return rejectWithValue(
        err?.response?.data?.detail ?? 'Failed to load file questions'
      );
    }
  }
);

const slice = createSlice({
  name: 'surveyFileQuestions',
  initialState,
  reducers: {
    invalidateFileQuestions(state, action: PayloadAction<number>) {
      const surveyId = action.payload;
      Object.keys(state.byKey).forEach((k) => {
        if (k.startsWith(`${surveyId}:`)) {
          delete state.byKey[k];
        }
      });
    },
  },
  extraReducers(builder) {
    builder
      .addCase(fetchFileQuestions.pending, (state, action) => {
        const {
          surveyId,
          actingOnMemberId = null,
          forceRefresh = false,
        } = action.meta.arg;
        const key = cacheKey(surveyId, actingOnMemberId);
        const entry = state.byKey[key];
        if (
          !forceRefresh &&
          entry?.status === 'succeeded' &&
          entry.lastFetchedAt &&
          Date.now() - entry.lastFetchedAt < STALE_MS
        ) {
          return;
        }
        state.byKey[key] = {
          status: 'loading',
          cards: entry?.cards ?? [], // preserve stale cards while refetching
          error: null,
          lastFetchedAt: entry?.lastFetchedAt ?? null,
        };
      })
      .addCase(fetchFileQuestions.fulfilled, (state, action) => {
        if (action.payload === null) {
          return;
        }
        const { surveyId, actingOnMemberId = null } = action.meta.arg;
        const key = cacheKey(surveyId, actingOnMemberId);
        state.byKey[key] = {
          status: 'succeeded',
          cards: action.payload.cards,
          error: null,
          lastFetchedAt: Date.now(),
        };
      })
      .addCase(fetchFileQuestions.rejected, (state, action) => {
        const { surveyId, actingOnMemberId = null } = action.meta.arg;
        const key = cacheKey(surveyId, actingOnMemberId);
        const entry = state.byKey[key];
        state.byKey[key] = {
          status: 'failed',
          cards: entry?.cards ?? [], // preserve stale cards on failure
          error: action.payload as string,
          lastFetchedAt: entry?.lastFetchedAt ?? null,
        };
      });
  },
});

export const { invalidateFileQuestions } = slice.actions;

export const selectFileQuestions =
  (surveyId: number, actingOnMemberId: number | null) =>
  (state: { surveyFileQuestions: State }): Entry | undefined =>
    state.surveyFileQuestions.byKey[cacheKey(surveyId, actingOnMemberId)];

export default slice.reducer;
